import React, { FormEvent, useRef } from "react";
import { ExpenseItem } from "../../shared/expenseItems";
import { ExpenseUserOption } from "../../shared/expenseUsersApi";
import ReceiptDetailsFields from "./ReceiptDetailsFields";
import ReceiptLineItems from "./ReceiptLineItems";
import {
  ReceiptConfidenceFields,
  ReceiptReviewDraft,
  ReceiptReviewErrors,
} from "./receiptReviewTypes";

export type { ReceiptReviewDraft } from "./receiptReviewTypes";

interface ReceiptReviewFormProps {
  title: string;
  subtitle: string;
  previewUrl?: string | null;
  previewMimeType?: string | null;
  previewLoading?: boolean;
  previewError?: string;
  fields?: ReceiptConfidenceFields;
  userOptions?: ExpenseUserOption[];
  userOptionsLoading?: boolean;
  draft: ReceiptReviewDraft;
  items: ExpenseItem[];
  busy?: boolean;
  error?: string;
  errors?: ReceiptReviewErrors;
  submitLabel: string;
  busyLabel?: string;
  receiptFileName?: string;
  onReceiptFileChange?: (file: File) => void;
  onDraftChange: (name: keyof ReceiptReviewDraft, value: string) => void;
  onItemsChange: (items: ExpenseItem[]) => void;
  onCancel: () => void;
  onSubmit: () => void | Promise<void>;
}

export default function ReceiptReviewForm({
  title,
  subtitle,
  previewUrl,
  previewMimeType,
  previewLoading = false,
  previewError = "",
  fields = {},
  userOptions = [],
  userOptionsLoading = false,
  draft,
  items,
  busy = false,
  error = "",
  errors = {},
  submitLabel,
  busyLabel = "Saving…",
  receiptFileName = "",
  onReceiptFileChange,
  onDraftChange,
  onItemsChange,
  onCancel,
  onSubmit,
}: ReceiptReviewFormProps) {
  const receiptInputRef = useRef<HTMLInputElement>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onSubmit();
  };

  const preview = previewLoading ? (
    <p className="py-12 text-center text-sm text-gray-500">Loading receipt…</p>
  ) : previewError ? (
    <p className="py-12 text-center text-sm text-red-600">{previewError}</p>
  ) : previewUrl ? (
    previewMimeType === "application/pdf" ? (
      <iframe
        title="Receipt preview"
        src={previewUrl}
        className="h-[520px] w-full rounded-md border"
      />
    ) : (
      <img
        src={previewUrl}
        alt="Receipt preview"
        className="max-h-[520px] w-full rounded-md object-contain"
      />
    )
  ) : (
    <p className="py-12 text-center text-sm text-gray-500">No receipt attached.</p>
  );

  return (
    <form onSubmit={submit} className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
        </div>
        <button type="button" onClick={onCancel} aria-label="Close">
          ✕
        </button>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div
          className={`rounded-lg bg-gray-50 p-3 ${
            onReceiptFileChange ? "border-2 border-dashed border-gray-300" : ""
          }`}
          onDragOver={(event) => {
            if (onReceiptFileChange) event.preventDefault();
          }}
          onDrop={(event) => {
            if (!onReceiptFileChange) return;
            event.preventDefault();
            const droppedFile = event.dataTransfer.files?.[0];
            if (droppedFile) onReceiptFileChange(droppedFile);
          }}
        >
          {preview}
          {onReceiptFileChange && (
            <div className="mt-3 text-center">
              <input
                ref={receiptInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                className="hidden"
                onChange={(event) => {
                  const selectedFile = event.target.files?.[0];
                  if (selectedFile) onReceiptFileChange(selectedFile);
                }}
              />
              <button
                type="button"
                className="rounded-md bg-white px-3 py-2 text-sm font-medium text-primary-600 shadow-sm"
                onClick={() => receiptInputRef.current?.click()}
              >
                {previewUrl ? "Replace receipt" : "Choose receipt"}
              </button>
              {receiptFileName && (
                <p className="mt-2 truncate text-xs text-gray-500">{receiptFileName}</p>
              )}
            </div>
          )}
        </div>
        <ReceiptDetailsFields
          draft={draft}
          fields={fields}
          userOptions={userOptions}
          userOptionsLoading={userOptionsLoading}
          errors={errors}
          onChange={onDraftChange}
        />
      </div>

      <ReceiptLineItems currency={draft.currency} items={items} onChange={onItemsChange} />

      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-md bg-gray-100 px-4 py-2 text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {busy ? busyLabel : submitLabel}
        </button>
      </div>
    </form>
  );
}
